import React, { type ReactNode } from 'react';
import { FlagProvider } from '@unleash/proxy-client-react';
// @ts-ignore - packages tsconfig uses moduleResolution: "node" which can't resolve subpath exports;
// the web tsconfig (used at build time) resolves these correctly via Vite.
import '@unleash/toolbar/toolbar.css';
// @ts-ignore - same as above
import { UnleashToolbarProvider } from '@unleash/toolbar/react';

import { ConfigService } from '@packages/core/config';

import { getContainer } from '../container';
import { resolveEnvironment, resolveHost } from '../runtime';
import { buildUnleashConfig, isToolbarEnabled } from './feature-flags.helpers';
import { useRuntimeContextSync } from './feature-flags.sync';

/**
 * Internal child component that runs inside the Unleash provider context.
 * Calls `useRuntimeContextSync` to push runtime context to the SDK and toolbar.
 */
function FeatureFlagContextSync({ children }: { children: ReactNode }) {
    useRuntimeContextSync();
    return <>{children}</>;
}

// Resolve config once at module scope
const configService = getContainer().resolve(ConfigService);
const unleashConfig = buildUnleashConfig(configService);
const environment = resolveEnvironment(configService.debugEnvName);
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const host = resolveHost(environment, hostname);

/** Development hosts get the toolbar; stage and production get the standard FlagProvider. */
const Provider = isToolbarEnabled(host) ? UnleashToolbarProvider : FlagProvider;

/** Fallback config used when no client key is configured. */
const FALLBACK_CONFIG = { url: '', clientKey: '', appName: 'techshare' };

type FeatureFlagProviderProps = {
    /** Child components that will have access to feature flags. */
    children: ReactNode;
};

/**
 * Top-level feature flag provider. Wraps the app with the Unleash SDK, configures the
 * toolbar for development hosts, and syncs runtime context after auth.
 *
 * No props needed; configuration is resolved internally from the IoC container.
 *
 * @param props - children to render inside the provider
 *
 * @example
 * ```tsx
 * <FeatureFlagProvider>
 *     <App />
 * </FeatureFlagProvider>
 * ```
 */
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
    return (
        <Provider
            config={unleashConfig ?? FALLBACK_CONFIG}
            toolbarOptions={{ position: 'bottom-right', storageMode: 'local', themePreset: 'dark' }}
        >
            <FeatureFlagContextSync>{children}</FeatureFlagContextSync>
        </Provider>
    );
}
